/**
 * Filter Configuration Definitions
 * 
 * This file contains the configuration for filter panels across different tabs.
 * Each configuration defines the fields, their types, and how they should be rendered.
 */

import { Search, Calendar, User, Building2 } from 'lucide-react';
// Note: Clock and Plane icons removed as they're no longer used
import React from 'react';
import { STATUS_OPTIONS } from '../utils/webcheckinStatus';

/**
 * Web Check-in Filter Configuration
 * Used for: webCheckIn, b2bWebCheckIn tabs
 */
export const webCheckInFilterConfig = {
  fields: [
    {
      type: 'text',
      key: 'email',
      label: 'Email',
      placeholder: 'Email...',
      tier: 'primary',
      span: 4,
      showLabel: false,
      icon: <Search className="h-4 w-4" />
    },
    {
      type: 'text',
      key: 'pnr',
      label: 'PNR',
      placeholder: 'PNR...',
      tier: 'primary',
      span: 3,
      showLabel: false
    },
    {
      type: 'text',
      key: 'userId',
      label: 'User ID',
      placeholder: 'User ID...',
      tier: 'primary',
      span: 3,
      showLabel: false,
      icon: <User className="h-4 w-4" />
    },
    {
      type: 'dateRange',
      key: 'departureDate',
      stateKeys: ['departureDateMin', 'departureDateMax'],
      label: 'Departure Date',
      tier: 'primary',
      span: 4,
      showLabel: false,
      icon: <Calendar className="h-4 w-4" />
    }
  ]
};

/**
 * External Partner Check-in Filter Configuration
 * Used for: extPartnerCheckin tab
 */
export const extPartnerFilterConfig = {
  fields: [
    {
      type: 'searchGroup',
      key: 'search',
      label: 'Search By',
      tier: 'primary',
      span: 5,
      searchFieldKey: 'searchField',
      searchValueKey: 'searchValue',
      icon: <Search className="h-4 w-4" />,
      searchOptions: [
        { value: 'pnr', label: 'PNR' },
        { value: 'partnerName', label: 'Partner Name' },
        { value: 'airlineName', label: 'Airline Name' },
        { value: 'subscriptionId', label: 'Subscription ID' },
        { value: 'flightNumber', label: 'Flight Number' }
      ],
      defaultSearchField: 'pnr'
    },
    {
      type: 'select',
      key: 'checkinStatus',
      label: 'Status',
      placeholder: 'Select status',
      tier: 'primary',
      span: 3,
      options: [{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]
    },
    {
      type: 'dateRange',
      key: 'departureDate',
      stateKeys: ['departureDateMin', 'departureDateMax'],
      label: 'Departure Date Range',
      tier: 'advanced',
      span: 12,
      icon: <Calendar className="h-4 w-4" />
    }
  ]
};

/**
 * Business4Flyo Check-in Filter Configuration
 * Used for: business4FlyoWebCheckIn tab
 */
export const business4FlyoFilterConfig = {
  fields: [
    {
      type: 'text',
      key: 'keyword',
      label: 'Search',
      placeholder: 'Search...',
      tier: 'primary',
      span: 4,
      showLabel: false,
      icon: <Search className="h-4 w-4" />
    },
    {
      type: 'text',
      key: 'agentId',
      label: 'Agent ID',
      placeholder: 'Agent ID...',
      tier: 'primary',
      span: 2,
      showLabel: false,
      icon: <Building2 className="h-4 w-4" />
    },
    {
      type: 'dateRange',
      key: 'departureDate',
      stateKeys: ['startDepartureDate', 'endDepartureDate'],
      label: 'Departure Date',
      tier: 'primary',
      span: 3,
      showLabel: false,
      icon: <Calendar className="h-4 w-4" />
    }
  ]
};

/**
 * Date preset options for quick date selection
 */
export const datePresets = [
  { key: 'today', label: 'Today', days: 0 },
  { key: 'tomorrow', label: 'Tomorrow', days: 1 },
  { key: 'next7days', label: 'Next 7 Days', days: 7 },
  { key: 'next30days', label: 'Next 30 Days', days: 30 }
];

/**
 * Get field configuration by key
 * @param {Object} config - Filter configuration object
 * @param {string} key - Field key to find
 * @returns {Object|undefined} Field configuration
 */
export const getFieldConfig = (config, key) => {
  return config.fields.find(field => 
    field.key === key || 
    (field.stateKeys && field.stateKeys.includes(key))
  );
};

/**
 * Get field label by key
 * @param {Object} config - Filter configuration object
 * @param {string} key - Field key
 * @returns {string} Field label
 */
export const getFieldLabel = (config, key) => {
  const field = getFieldConfig(config, key);
  return field?.label || key;
};
